import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { EstablishmentBadge, AudienceChip } from "../../lib/clinicMeta";
import { Building2, MapPin, ArrowRight, UserCircle2, Award, Sparkles, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * FeaturedCarousel — carrossel de destaques da Home.
 *
 * Fetcha só entidades com selo Ouro. Renderiza em grupos de 3 e auto-rotaciona
 * a cada 6s quando houver mais de 3. Usa fade + slide.
 *
 * Props:
 *   kind: "clinicas" | "profissionais"
 */
const AUTO_ROTATE_MS = 6000;

const TierBadge = ({ tier }) => (
  <span className={`text-[9px] font-black px-1.5 py-0.5 rounded uppercase tracking-widest ${
    tier === "ouro" ? "bg-gradient-to-r from-[#F1C232] to-[#D4A017] text-[#3B2F0B]" :
    tier === "prata" ? "bg-gradient-to-r from-slate-300 to-slate-500 text-slate-900" :
    "bg-gradient-to-r from-[#CD7F32] to-[#8B4513] text-white"
  }`}>{tier}</span>
);

const ClinicCard = ({ c }) => {
  const audArr = Array.isArray(c.audience) ? c.audience : [];
  return (
    <Link to={`/unidades/${c.id}`} data-testid={`featured-clinic-${c.id}`}
      className="group bg-white border border-slate-200 rounded-xl overflow-hidden hover:border-[#0B2B62] transition-all hover:shadow-lg flex flex-col">
      <div className="aspect-[16/10] bg-slate-100 overflow-hidden relative">
        {c.hero_image ? (
          <img src={c.hero_image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-300"><Building2 className="w-12 h-12" /></div>
        )}
        {c.establishment_type && (
          <div className="absolute top-2 left-2"><EstablishmentBadge type={c.establishment_type} size="sm" /></div>
        )}
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-mono uppercase tracking-widest bg-[#0B2B62] text-white px-1.5 py-0.5 rounded">{c.code}</span>
          <h3 className="font-display font-bold text-lg text-[#0B2B62]">Clínica {c.code}</h3>
          {c.certification_tier && <TierBadge tier={c.certification_tier} />}
        </div>
        <p className="text-xs text-slate-500 flex items-center gap-1 mt-1"><MapPin className="w-3 h-3" /> {c.city}, {c.state}</p>
        {audArr.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {audArr.slice(0, 3).map((a, i) => <AudienceChip key={`${a}-${i}`} audience={a} />)}
          </div>
        )}
        <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-[#0B2B62] font-semibold inline-flex items-center gap-1">
          Ver detalhes <ArrowRight className="w-3 h-3" />
        </div>
      </div>
    </Link>
  );
};

const ProfCard = ({ p }) => (
  <Link to={`/profissionais/${p.id}`} data-testid={`featured-prof-${p.id}`}
    className="group bg-white border border-slate-200 rounded-xl overflow-hidden hover:border-emerald-600 transition-all hover:shadow-lg flex flex-col">
    <div className="aspect-[4/5] bg-gradient-to-br from-emerald-50 to-emerald-100 overflow-hidden relative flex items-center justify-center">
      {p.photo_url ? (
        <img src={p.photo_url} alt="" className="w-full h-full object-cover object-[center_15%] group-hover:scale-105 transition-transform duration-500" />
      ) : (
        <UserCircle2 className="w-20 h-20 text-emerald-300" />
      )}
      <div className="absolute top-2 left-2 text-[10px] uppercase tracking-widest bg-emerald-700 text-white px-2 py-0.5 rounded-full font-bold inline-flex items-center gap-1">
        <Sparkles className="w-3 h-3" /> Profissional
      </div>
    </div>
    <div className="p-4 flex-1 flex flex-col">
      <div className="flex items-center gap-2 flex-wrap">
        <h3 className="font-display font-bold text-base text-[#0B2B62] leading-tight">{p.display_name || "Profissional RAF"}</h3>
        {p.certification_tier && <TierBadge tier={p.certification_tier} />}
      </div>
      {p.categories?.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {p.categories.slice(0, 2).map((c) => (
            <span key={c} className="text-[10px] uppercase tracking-widest bg-emerald-50 text-emerald-800 border border-emerald-200 px-2 py-0.5 rounded-full font-semibold">{c}</span>
          ))}
        </div>
      )}
      {p.bio && <p className="text-xs text-slate-600 mt-2 line-clamp-2">{p.bio}</p>}
      <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-emerald-700 font-semibold inline-flex items-center gap-1">
        Ver perfil <ArrowRight className="w-3 h-3" />
      </div>
    </div>
  </Link>
);

const FeaturedCarousel = ({ kind }) => {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const url = kind === "clinicas" ? "/public/clinics" : "/professionals";
    api.get(url).then(({ data }) => {
      const list = Array.isArray(data) ? data : [];
      // Filtra só Ouro pra ser realmente "destaque"
      const ouro = list.filter((x) => (x.certification_tier || "").toLowerCase() === "ouro");
      // Embaralha (Fisher-Yates) para que a cada page load um destaque diferente
      // fique em primeiro — todos são Ouro, então é justo rotacionar posição.
      for (let i = ouro.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [ouro[i], ouro[j]] = [ouro[j], ouro[i]];
      }
      setItems(ouro);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [kind]);

  // Divide em páginas de 3
  const pages = useMemo(() => {
    const chunks = [];
    for (let i = 0; i < items.length; i += 3) chunks.push(items.slice(i, i + 3));
    return chunks;
  }, [items]);

  // Auto-rotate a cada 6s: se tiver >1 página, rotaciona a página; se tiver só
  // 1 página mas >1 item, embaralha o próprio conteúdo pra alternar quem
  // fica em primeiro sem trocar de página.
  useEffect(() => {
    if (pages.length === 0) return;
    const id = setInterval(() => {
      if (pages.length > 1) {
        setPage((p) => (p + 1) % pages.length);
      } else if (items.length > 1) {
        setItems((prev) => {
          const next = [...prev];
          for (let i = next.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [next[i], next[j]] = [next[j], next[i]];
          }
          return next;
        });
      }
    }, AUTO_ROTATE_MS);
    return () => clearInterval(id);
  }, [pages.length, items.length]);

  if (loading || items.length === 0) return null;

  const cfg = kind === "clinicas"
    ? {
        eyebrow: "Rede RAF Direct",
        title: "Unidades Parceiras em Destaque",
        subtitle: "Clínicas com certificação Ouro na rede RAF — auditoria contínua, atendimento presencial verificado.",
        cta: { to: "/unidades", label: "Ver todas as unidades" },
        Card: ClinicCard,
        color: "text-[#0B2B62]",
      }
    : {
        eyebrow: "RAF Direct",
        title: "Profissionais em Destaque",
        subtitle: "Profissionais com certificação Ouro — psiquiatras, psicólogos, terapeutas e mais, todos verificados pela RAF.",
        cta: { to: "/profissionais", label: "Ver todos os profissionais" },
        Card: ProfCard,
        color: "text-emerald-700",
      };

  const current = pages[page] || [];

  return (
    <section className="max-w-7xl mx-auto px-6 py-14" data-testid={`featured-${kind}`}>
      <div className="mb-8 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className={`text-xs uppercase tracking-[0.18em] ${cfg.color} font-semibold inline-flex items-center gap-1.5`}>
            <Award className="w-3.5 h-3.5" /> {cfg.eyebrow}
          </div>
          <h2 className="font-display font-bold text-[#0B2B62] text-3xl md:text-4xl mt-2 tracking-tight">
            {cfg.title}
          </h2>
          <p className="text-sm text-slate-500 mt-2 max-w-xl">{cfg.subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          {pages.length > 1 && (
            <>
              <button onClick={() => setPage((p) => (p - 1 + pages.length) % pages.length)}
                aria-label="Anterior"
                data-testid={`featured-${kind}-prev`}
                className="p-2 rounded-full border border-slate-200 hover:border-[#0B2B62] hover:bg-[#0B2B62] hover:text-white transition-colors text-slate-500">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button onClick={() => setPage((p) => (p + 1) % pages.length)}
                aria-label="Próximo"
                data-testid={`featured-${kind}-next`}
                className="p-2 rounded-full border border-slate-200 hover:border-[#0B2B62] hover:bg-[#0B2B62] hover:text-white transition-colors text-slate-500">
                <ChevronRight className="w-4 h-4" />
              </button>
            </>
          )}
          <Link to={cfg.cta.to} data-testid={`featured-${kind}-see-all`}
            className="ml-2 text-sm font-semibold text-[#0B2B62] hover:underline inline-flex items-center gap-1">
            {cfg.cta.label} <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>

      <div key={`${page}-${items.map(i=>i.id).join(",").slice(0,32)}`} className="grid grid-cols-1 md:grid-cols-3 gap-5 animate-in fade-in duration-500">
        {current.map((x) => <cfg.Card key={x.id} c={x} p={x} />)}
      </div>

      {pages.length > 1 && (
        <div className="mt-6 flex justify-center gap-1.5" data-testid={`featured-${kind}-dots`}>
          {pages.map((_, i) => (
            <button key={i} onClick={() => setPage(i)}
              aria-label={`Página ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${i === page ? "w-8 bg-[#0B2B62]" : "w-1.5 bg-slate-300 hover:bg-slate-400"}`} />
          ))}
        </div>
      )}
    </section>
  );
};

export default FeaturedCarousel;
